import { isToday, parseISO, startOfMonth, startOfYear, subDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { DashboardKPIs } from '../types/dashboard.types';

// Type pour les données booking transformées pour le dashboard
interface DashboardBooking {
  id: string;
  ref: string;
  client: string;
  type: string; // SpaceType enum value
  spaceId: string;
  spaceName: string; // Nom de l'espace
  status: string;
  workflow: string;
  isUrgent: boolean;
  amount: number;
  date: string; // Date formatée pour l'affichage
  dateISO: string; // Date ISO pour le filtrage
}

export function useDashboardData(filters: { typeFilter: string; statusFilter: string; dateFilter: string }) {
  const { typeFilter, statusFilter, dateFilter } = filters;

  const [allBookings, setAllBookings] = useState<DashboardBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Chargement des statistiques depuis l'endpoint dédié
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Charger les stats et un échantillon de bookings récentes
        // IMPORTANT: On passe status=all pour récupérer TOUS les statuts (sauf exclusions SingPay)
        // Le filtrage par statut (PAID, PENDING, etc.) se fait côté client via le dropdown
        // Cela permet au dashboard d'avoir une vue complète et de calculer tous les KPIs
        const [statsRes, bookingsRes] = await Promise.all([
          api.get('/bookings/admin/stats'),
          api.get('/bookings/admin/all?page=1&pageSize=100&status=all'), // Tous statuts, exclusionsSingPay seulement
        ]);

        if (!statsRes.data.success || !bookingsRes.data.success) {
          throw new Error('Erreur lors de la récupération des données');
        }

        const { items } = bookingsRes.data.data;

        // Transformer les données pour le dashboard
        const transformed = items
          .map((item: any) => {
            // Utiliser le nom du spaceType ou un fallback basé sur le type
            const spaceName = item.spaceType?.name || getSpaceTypeFallbackName(item.spaceType?.slug);

            // LOGIQUE DE DATE OPTIMALE (Phase 2)
            // Priorité 1 : paidAt (date de paiement précise) pour PAID/COMPLETED
            // Priorité 2 : updatedAt (approximation) pour PAID/COMPLETED sans paidAt
            // Priorité 3 : startDate (date de réservation) pour autres statuts
            // Priorité 4 : createdAt (fallback de sécurité)
            const isPaid = ['PAID', 'COMPLETED'].includes(item.status);
            let relevantDate: string;

            if (isPaid && item.paidAt) {
              // Phase 2 : Utiliser paidAt si disponible (précis)
              relevantDate = item.paidAt;
            } else if (isPaid && item.updatedAt) {
              // Phase 1 : Fallback sur updatedAt (approximation)
              relevantDate = item.updatedAt;
            } else if (item.startDate) {
              // Statuts non payés : utiliser date de réservation
              relevantDate = item.startDate;
            } else {
              // Fallback de sécurité : createdAt (toujours présent)
              relevantDate = item.createdAt;
            }

            return {
              id: String(item.id),
              ref: item.reference,
              client: item.customerName,
              type: mapSpaceType(item),
              spaceId: item.spaceType?.id || item.spaceTypeId || 'unknown',
              spaceName,
              status: item.status,
              workflow: item.workflow,
              isUrgent: !!item.isUrgent,
              amount: Number(item.totalAmount),
              date: new Date(relevantDate).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }),
              dateISO: relevantDate,
            } as DashboardBooking;
          })
          // Filtrage de sécurité : on ignore le bruit SingPay
          .filter((b: DashboardBooking) => {
            // Exclure les transactions immédiates non payées
            if (b.status === 'PAYMENT_PENDING' && b.workflow === 'IMMEDIATE') return false;
            // Exclure les devis urgents en attente de paiement des frais
            if (b.status === 'PENDING' && b.isUrgent) return false;
            return true;
          });

        setAllBookings(transformed);
      } catch (err: any) {
        console.error('[useDashboardData] Fetch error:', err);
        setError(err.message || 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Filtrage client-side (instantané)
  // Note: Les données de base sont déjà chargées avec status=PAID (PAID+COMPLETED)
  // Ce filtrage permet d'affiner davantage sans requête serveur supplémentaire.
  // Les alias (PAID, PENDING, QUOTE_REQUESTED) doivent être cohérents avec
  // les expansions côté serveur dans BookingService.ts.
  const filteredBookings = useMemo(() => {
    if (loading || error) return [];

    return allBookings.filter((b) => {
      const matchType = typeFilter === 'all' || b.spaceName === typeFilter;

      let matchStatus = statusFilter === 'all';
      if (!matchStatus) {
        // Alias UI → statuts Booking réels (identique au backend)
        if (statusFilter === 'PAID') {
          matchStatus = ['PAID', 'COMPLETED'].includes(b.status);
        } else if (statusFilter === 'PENDING') {
          matchStatus = [
            'PENDING',
            'CONFIRMED',
            'PAYMENT_PENDING',
            'APPROVED',
            'QUOTE_REQUESTED',
            'QUOTED',
            'URGENT',
            'OVERDUE',
          ].includes(b.status);
        } else if (statusFilter === 'QUOTE_REQUESTED') {
          matchStatus = ['QUOTE_REQUESTED', 'QUOTED', 'URGENT', 'OVERDUE'].includes(b.status);
        } else {
          matchStatus = b.status === statusFilter;
        }
      }

      let matchDate = true;
      if (dateFilter !== 'all' && b.dateISO) {
        const d = parseISO(b.dateISO);
        const now = new Date();
        if (dateFilter === 'today') matchDate = isToday(d);
        else if (dateFilter === '7days') matchDate = d >= subDays(now, 7);
        else if (dateFilter === 'month') matchDate = d >= startOfMonth(now);
        else if (dateFilter === 'year') matchDate = d >= startOfYear(now);
      }

      return matchType && matchStatus && matchDate;
    });
  }, [allBookings, typeFilter, statusFilter, dateFilter, loading, error]);

  // Liste des noms d'espaces uniques pour le filtre
  const uniqueSpaceNames = useMemo(() => {
    const names = new Set(allBookings.map((b) => b.spaceName));
    return Array.from(names).sort();
  }, [allBookings]);

  // KPIs calculés sur TOUTES les bookings (non filtrées) - Les KPI doivent rester fixes
  const kpis: DashboardKPIs = useMemo(() => {
    const totalVolume = allBookings.reduce((sum, b) => sum + b.amount, 0);

    // CA Réalisé : Payé ou Terminé
    const paidRevenue = allBookings
      .filter((b) => b.status === 'PAID' || b.status === 'COMPLETED')
      .reduce((sum, b) => sum + b.amount, 0);

    // CA En Attente : Statuts actifs avant paiement (Confirmé ou Paiement en attente)
    const pendingRevenue = allBookings
      .filter((b) => {
        // Logique "En attente" : Tout ce qui n'est pas encore payé (PAID/COMPLETED)
        // ou annulé/rejeté, est considéré comme du CA en cours de traitement.
        return [
          'PENDING',
          'CONFIRMED',
          'PAYMENT_PENDING',
          'APPROVED',
          'URGENT',
          'OVERDUE',
          'QUOTE_REQUESTED',
          'QUOTED',
        ].includes(b.status);
      })
      .reduce((sum, b) => sum + b.amount, 0);

    // CA sur Devis : Uniquement les revenus issus du workflow Devis qui sont PAYÉS
    const quoteRevenue = allBookings
      .filter((b) => b.workflow === 'QUOTE_REQUIRED' && ['PAID', 'COMPLETED'].includes(b.status))
      .reduce((sum, b) => sum + b.amount, 0);

    const typesCount = allBookings.reduce(
      (acc, b) => {
        const name = b.spaceName;
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return { totalVolume, paidRevenue, pendingRevenue, quoteRevenue, typesCount };
  }, [allBookings]);

  // Pie chart data
  const pieData = useMemo(() => {
    return Object.entries(kpis.typesCount).map(([name, count]) => ({
      name,
      value: count,
    }));
  }, [kpis.typesCount]);

  // Revenue trend chart
  // IMPORTANT: Utilise allBookings (non filtré) pour afficher l'évolution GLOBALE
  // Les filtres de type/statut ne doivent PAS affecter ce graphique
  const revenueTrendData = useMemo(() => {
    const isYearOrAll = dateFilter === 'year' || dateFilter === 'all';
    const now = new Date();

    // 1. Initialiser la structure de données avec tous les points de temps de la période
    const timeline: Record<string, { month: string; sortKey: string; paid: number; pending: number }> = {};

    if (dateFilter === '7days') {
      for (let i = 6; i >= 0; i--) {
        const d = subDays(now, i);
        const sortKey = d.toISOString().substring(5, 10);
        timeline[sortKey] = {
          month: d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
          sortKey,
          paid: 0,
          pending: 0,
        };
      }
    } else if (dateFilter === 'month') {
      const start = startOfMonth(now);
      const daysInMonth = now.getDate();
      for (let i = 0; i < daysInMonth; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        const sortKey = d.toISOString().substring(5, 10);
        timeline[sortKey] = {
          month: d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
          sortKey,
          paid: 0,
          pending: 0,
        };
      }
    } else if (dateFilter === 'year') {
      for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), i, 1);
        const sortKey = d.toISOString().substring(0, 7);
        timeline[sortKey] = {
          month: d.toLocaleDateString('fr-FR', { month: 'short' }),
          sortKey,
          paid: 0,
          pending: 0,
        };
      }
    }

    // 2. Remplir avec les données réelles (TOUTES les bookings, pas filtrées)
    allBookings.forEach((b) => {
      const d = parseISO(b.dateISO);
      const sortKey = isYearOrAll ? b.dateISO.substring(0, 7) : b.dateISO.substring(5, 10);

      if (!timeline[sortKey]) {
        // Fallback pour 'all' ou dates hors plage générée
        const displayLabel = isYearOrAll
          ? d.toLocaleDateString('fr-FR', { month: 'short' })
          : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
        timeline[sortKey] = { month: displayLabel, sortKey, paid: 0, pending: 0 };
      }

      if (['PAID', 'COMPLETED'].includes(b.status)) {
        timeline[sortKey].paid += b.amount;
      } else if (
        ['PENDING', 'CONFIRMED', 'PAYMENT_PENDING', 'APPROVED', 'QUOTE_REQUESTED', 'QUOTED', 'URGENT'].includes(
          b.status,
        )
      ) {
        // Exclure bruits SingPay
        const isNoise =
          (b.status === 'PAYMENT_PENDING' && b.workflow === 'IMMEDIATE') || (b.status === 'PENDING' && b.isUrgent);
        if (!isNoise) {
          timeline[sortKey].pending += b.amount;
        }
      }
    });

    return Object.values(timeline).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [allBookings, dateFilter]);

  // Top spaces data
  const topSpacesData = useMemo(() => {
    const grouped = filteredBookings.reduce(
      (acc, b) => {
        if (!acc[b.spaceId]) {
          acc[b.spaceId] = {
            id: b.spaceId,
            name: b.spaceName,
            count: 0,
            revenue: 0,
          };
        }
        acc[b.spaceId].count += 1;
        acc[b.spaceId].revenue += b.amount;
        return acc;
      },
      {} as Record<string, { id: string; name: string; count: number; revenue: number }>,
    );

    return Object.values(grouped)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 4);
  }, [filteredBookings]);

  return {
    filteredBookings,
    allBookings, // Pour le total global dans la barre de filtres
    uniqueSpaceNames,
    kpis,
    pieData,
    revenueTrendData,
    topSpacesData,
    loading,
    error,
  };
}

// Mapping des slugs vers SpaceType pour le dashboard (Diamond Standard)
function mapSpaceType(item: any): string {
  const slug = item.spaceType?.slug?.toLowerCase();
  if (!slug) return 'COWORKING';

  // Mapping identique à useBookingsAdmin pour cohérence totale
  const mapping: Record<string, string[]> = {
    COWORKING: ['espace-coworking', 'coworking'],
    MEETING_ROOM: ['salle-conference', 'salle-de-conference', 'studio-sing', 'studio'],
    PRIVATE_OFFICE: ['bureau-permanent', 'bureau-prive', 'bureau-privatif'],
    EVENT_SPACE: ['espace-evenementiel', 'evenementiel', 'terrasse'],
  };

  for (const [type, slugs] of Object.entries(mapping)) {
    if (slugs.includes(slug)) return type;
  }

  // Fallback par mots-clés dans le slug
  if (slug.includes('salle') || slug.includes('conference') || slug.includes('studio')) return 'MEETING_ROOM';
  if (slug.includes('bureau')) return 'PRIVATE_OFFICE';
  if (slug.includes('evenement') || slug.includes('terrasse')) return 'EVENT_SPACE';

  // Fallback par catégorie native Prisma
  const category = item.spaceType?.category?.toUpperCase();
  if (category === 'COWORKING') return 'COWORKING';
  if (category === 'OFFICE' || category === 'PRIVATE_OFFICE') return 'PRIVATE_OFFICE';
  if (category === 'MEETING' || category === 'MEETING_ROOM') return 'MEETING_ROOM';
  if (category === 'EVENT' || category === 'EVENT_SPACE') return 'EVENT_SPACE';

  return 'COWORKING';
}

// Fallback pour les noms d'espaces basés sur le slug
function getSpaceTypeFallbackName(slug?: string): string {
  if (!slug) return 'Espace Coworking';

  const mapping: Record<string, string> = {
    'espace-coworking': 'Espace Coworking',
    'salle-conference': 'Salle de Conférence',
    'bureau-permanent': 'Bureau Permanent',
    'espace-evenementiel': 'Espace Événementiel',
  };

  // Si le slug est dans le mapping, utiliser le nom mappé
  if (mapping[slug]) {
    return mapping[slug];
  }

  // Sinon, transformer le slug en nom lisible
  // Ex: "mon-espace-test" -> "Mon Espace Test"
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
